import { operators as o, common as c } from '@pendo/aggregations';
import cloneDeep from 'lodash/cloneDeep';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';

/**
 * Aggregation for product area hover card metrics.
 * Uses o.spawn() to union all 3 event type streams, then o.reduce() to count
 * truly unique visitors/accounts across all event types for this product area.
 */

const KIND_SCHEMAS = [
    { events: 'pageEvents', id: 'pageId', list: 'pages' },
    { events: 'featureEvents', id: 'featureId', list: 'features' },
    { events: 'trackEvents', id: 'trackTypeId', list: 'trackTypes' },
] as const;

export default {
    name: 'ProductAreaMetricsAggregation',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.PRODUCT_AREAS,
    responseFormat: 'row',
    build: ({
        appId,
        timeSeries,
        blacklist,
        includeAnonymous,
        groupId,
    }: {
        appId: c.AppIdType;
        timeSeries: c.TimeSeries;
        blacklist: c.BlacklistTypes;
        includeAnonymous?: string | boolean;
        groupId: string;
    }) => {
        const sourceParams: { timeSeries: c.TimeSeries; appId: c.AppIdType; blacklist?: c.BlacklistTypes } = {
            timeSeries: cloneDeep(timeSeries),
            appId,
        };

        if (blacklist) {
            sourceParams.blacklist = blacklist;
        }

        return o.pipeline(
            o.spawn(
                ...KIND_SCHEMAS.map(({ events, id, list }) =>
                    o.pipeline(
                        o.sources[events](sourceParams),
                        o.identified(includeAnonymous),
                        o.merge(
                            [id],
                            o.mappings({ group: 'group' }),
                            o.pipeline(
                                o.sources[list]({ appId }),
                                o.filter('!isNil(group.id)'),
                                o.evaluate(id, 'id'),
                            ),
                        ),
                        o.evaluate('groupId', 'group.id'),
                    ),
                ),
            ),
            o.filter(`groupId == "${groupId}"`),
            o.reduce({
                uniqueVisitors: o.count('visitorId'),
                uniqueAccounts: o.countIf('accountId', 'accountId != ""'),
            }),
        );
    },
};
